import {
  Component,
  Input,
  HostBinding,
  ElementRef,
  Optional,
  Self,
  OnDestroy
} from "@angular/core";
import { FormControl, ControlValueAccessor, NgControl } from "@angular/forms";
import { MatFormFieldControl } from "@angular/material/form-field";
import { FocusMonitor } from "@angular/cdk/a11y";

import { Subject, Subscription } from "rxjs";

import { BHRTime } from "../../../../../../common/interfaces/bhr";

// https://material.angular.io/guide/creating-a-custom-form-field-control

@Component({
  selector: "onsip-bhr-time-input",
  template: `<input matInput [formControl]="control" (input)="handleInput()" type="time" />`,
  providers: [{ provide: MatFormFieldControl, useExisting: BHRTimeInputComponent }],
  standalone: false
})
export class BHRTimeInputComponent
  implements ControlValueAccessor, MatFormFieldControl<BHRTime>, OnDestroy
{
  static nextId = 0;
  @HostBinding() id = `onsip-bhr-time-input-${BHRTimeInputComponent.nextId++}`;
  @HostBinding("attr.aria-describedby") describedBy = "";

  control = new FormControl("", { nonNullable: true });
  stateChanges = new Subject<void>();
  errorState = false;
  focused = false;
  readonly shouldLabelFloat = true;

  @Input()
  get value(): BHRTime | null {
    const bhrTime = this.mapStringToBHRTime(this.control.value);
    if (!!bhrTime.h && !!bhrTime.m) {
      return bhrTime;
    }
    // eslint-disable-next-line no-null/no-null
    return null;
  }
  set value(bhrTime: BHRTime | null) {
    if (bhrTime) {
      this.control.setValue(this.mapBHRTimeToString(bhrTime));
    } else {
      this.control.setValue("");
    }
    this.stateChanges.next();
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(ph) {
    this._placeholder = ph;
    this.stateChanges.next();
  }

  get empty() {
    return !!this.control.value;
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = !!value;
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = !!value;
    if (this._disabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
    this.stateChanges.next();
  }

  private _placeholder = "";
  private _required = false;
  private _disabled = false;
  private unsubscriber = new Subscription();

  constructor(
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl
  ) {
    this.unsubscriber.add(
      _focusMonitor.monitor(_elementRef, true).subscribe(origin => {
        if (this.focused && !origin) {
          this.onTouched();
        }
        this.focused = !!origin;
        this.stateChanges.next();
      })
    );

    // eslint-disable-next-line no-null/no-null
    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
      setTimeout(() => {
        if (this.ngControl.control instanceof FormControl && this.ngControl.statusChanges) {
          this.unsubscriber.add(
            this.ngControl.statusChanges.subscribe(status => {
              this.errorState = status === "INVALID";
              this.stateChanges.next();
            })
          );
        }
      });
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_: any) => {};
  onTouched = () => {};

  setDescribedByIds(ids: Array<string>) {
    this.describedBy = ids.join(" ");
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== "input") {
      const inputSelect = this._elementRef.nativeElement.querySelector("input");
      inputSelect && inputSelect.focus();
    }
  }

  registerOnChange(fn: (val: any) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }
  }

  writeValue(value: any) {
    this.value = value;
  }

  handleInput() {
    this.onChange(this.mapStringToBHRTime(this.control.value));
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
    this.unsubscriber.unsubscribe();
  }

  private mapStringToBHRTime(time: string): any {
    let newTime: any;
    if (time.length === 5) {
      newTime = { h: Number(time.substring(0, 2)), m: Number(time.substring(3, 8)) };
    } else {
      // incomplete or empty field stored as h: -1
      newTime = { h: -1, m: 0 };
    }
    return newTime;
  }

  private mapBHRTimeToString(time: any): string {
    if (time !== undefined && time.h !== undefined && time.m !== undefined) {
      if (time.h === -1) {
        return "";
      } else {
        return String("0" + time.h).slice(-2) + ":" + String("0" + time.m).slice(-2);
      }
    } else {
      return "";
    }
  }
}
